import * as React from "react";
import { styled } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MenuContent from "./MenuContent";
import OptionsMenu from "./OptionsMenu";
import HeaderLogo from "./HeaderLogo";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector, useDispatch } from "react-redux";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const drawerWidth = 240;
const collapsedWidth = 72;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "collapsed",
})(({ collapsed }) => ({
  width: collapsed ? collapsedWidth : drawerWidth,
  flexShrink: 0,
  boxSizing: "border-box",
  mt: 10,
  transition: "width 0.2s ease-in-out",
  [`& .${drawerClasses.paper}`]: {
    width: collapsed ? collapsedWidth : drawerWidth,
    boxSizing: "border-box",
    transition: "width 0.2s ease-in-out",
    overflowX: "hidden",
  },
}));

export default function SideMenu(props) {
  const { user } = useAuth0();
  const dispatch = useDispatch();
  const collapsed = useSelector((state) => state.sidebarCollapsed);

  const handleToggle = () => {
    dispatch({ type: "sidebarCollapsed/toggle" });
  };

  return (
    <Drawer
      variant="permanent"
      collapsed={collapsed}
      sx={{
        display: { xs: "none", md: "block" },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: "var(--bg-sidebar)",
          borderRight: "1px solid var(--border-subtle)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: "calc(var(--template-frame-height, 0px) + 4px)",
          p: 1.5,
        }}
      >
        <HeaderLogo collapsed={collapsed} />
        {!collapsed && (
          <Tooltip title="Fermer le menu" placement="bottom">
            <IconButton
              onClick={handleToggle}
              size="small"
              sx={{
                color: "var(--text-secondary)",
                borderRadius: 1,
                "&:hover": {
                  color: "var(--text-primary)",
                  backgroundColor: "var(--menu-item-hover)",
                },
              }}
            >
              <ChevronLeft sx={{ fontSize: 24, fontWeight: "bold" }} />
            </IconButton>
          </Tooltip>
        )}
        {collapsed && (
          <Tooltip title="Ouvrir le menu" placement="right">
            <IconButton
              onClick={handleToggle}
              size="small"
              sx={{
                color: "var(--text-secondary)",
                borderRadius: 1,
                "&:hover": {
                  color: "var(--text-primary)",
                  backgroundColor: "var(--menu-item-hover)",
                },
              }}
            >
              <ChevronRight sx={{ fontSize: 24, fontWeight: "bold" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider />
      <MenuContent collapsed={collapsed} />
      <Stack
        direction="row"
        sx={{
          p: collapsed ? 1 : 2,
          gap: 1,
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tooltip title={collapsed && user ? user.name : ""} placement="right">
          <Avatar
            sizes="small"
            alt={user ? user.name : ""}
            sx={{ width: 36, height: 36, flexShrink: 0 }}
          />
        </Tooltip>
        {!collapsed && (
          <>
            {/* minWidth: 0 permet au container flex de rétrécir et
                déclencher l'ellipsis sur ses enfants */}
            <Box sx={{ mr: "auto", minWidth: 0, flex: 1 }}>
              <Tooltip title={user?.name || ""} placement="top">
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    lineHeight: "16px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user ? user.name : ""}
                </Typography>
              </Tooltip>
              <Tooltip title={user?.email || ""} placement="top">
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user ? user.email : ""}
                </Typography>
              </Tooltip>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <OptionsMenu />
            </Box>
          </>
        )}
      </Stack>
    </Drawer>
  );
}
